import React from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import {
  FDContentContainer,
  FDPaper,
  FDStack,
  FDImage,
  FDBox, FDEmphasisSurface,
} from '@facet-wealth/design';
import { HeadlineTextMedium, HeadlineTextLarge, BodyTextMedium } from 'Packages/FacetDesign/Components';
import { useModalContextSubmissionSuccessful } from 'Hooks/useRefreshEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMobileLayout from 'Hooks/useMobileLayout';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { SecurityAlert } from 'Components/ComponentLibrary/Organisms/SecurityAlert';
import { dataAttrs } from 'Helpers/Testing';
import { PrimaryActionButtonRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/PrimaryActionButtonRel';
import { ClickableTextRel } from 'Packages/FacetCore/Experimental/RelationshipComponents/ClickableTextRel';

const metaOverrideSet = buildFacetDesignOverride()
  .nameOverride('name', { _component: () => HeadlineTextMedium }, overrideReadOnly)
  .nameOverride('title', { _component: () => HeadlineTextLarge }, overrideReadOnly)
  .nameOverride('description', { _component: () => BodyTextMedium }, overrideReadOnly)
  .nameOverride('securityMessage', {
    _component: () => SecurityAlert,
  }, overrideReadOnly);

export const SimpleHeroPaperEmptyPageTemplate = ({ resource, refreshAfterwards }) => {
  const { refreshPageContentCollection } = usePageContentContext();

  const isMobile = useMobileLayout();
  useBoneBackgroundColor();

  useModalContextSubmissionSuccessful((_, refreshAfterwardsDep, refreshPageContentCollectionDep) => {
    if (refreshAfterwardsDep) {
      refreshPageContentCollectionDep();
    }
  }, [refreshAfterwards, refreshPageContentCollection]);

  return (
    <FDContentContainer maxWidth="md" {...dataAttrs('empty-state-page', resource)}>
      <FDStack direction="column" spacing={4} paddingY={3}>
        <MetaControl resource={resource} attrName={'name'} metaOverrideSet={metaOverrideSet} />
        {resource.attr('securityMessage') ? <MetaControl resource={resource} attrName={'securityMessage'} metaOverrideSet={metaOverrideSet} /> : null}
        <MetaControl resource={resource} attrName="subHeader" metaOverrideSet={metaOverrideSet} />
        <FDPaper appearance="elevated">
          <FDStack direction="row">
            <FDBox padding={3}>
              <FDStack direction="column" spacing={3} flexGrow={1} height="100%" justifyContent="center">
                <MetaControl resource={resource} attrName={'title'} metaOverrideSet={metaOverrideSet} />
                <MetaControl resource={resource} attrName={'description'} metaOverrideSet={metaOverrideSet} />
                <PrimaryActionButtonRel
                  relationship={resource.relationship(fr.relationships.getStarted.name)}
                  {...dataAttrs('get-started-btn', resource)}
                />
                <ClickableTextRel
                  appearance="minor"
                  relationship={resource.relationship(fr.relationships.skip.name)}
                  {...dataAttrs('skip-btn', resource)}
                />
              </FDStack>
            </FDBox>
            {isMobile ? null : resource.withRelationship(fr.relationships.image.name,
              (rel) => (
                <FDStack maxWidth="500px" flexShrink={0}>
                  <FDEmphasisSurface padding={8}>
                    <FDImage src={rel.link('related').url()} alt="hero image" aspectRatio="382/259" width="100%" height="400px" />
                  </FDEmphasisSurface>
                </FDStack>
              ),
              () => {})}
          </FDStack>
        </FDPaper>
      </FDStack>
    </FDContentContainer>
  );
};

SimpleHeroPaperEmptyPageTemplate.defaultProps = {
  refreshAfterwards: true,
};

SimpleHeroPaperEmptyPageTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
  refreshAfterwards: PropTypes.bool,
};
